<script setup>
import { computed, onMounted, watch } from 'vue'

import CookieBanner from '@/components/shared/ui/CookieBanner.vue'
import DebugBanner from '@/components/shared/ui/DebugBanner.vue'
import MaintenanceBanner from '@/components/shared/ui/MaintenanceBanner.vue'
import QuotilaBanner from '@/components/shared/ui/QuotilaBanner.vue'
import TalkativeWidget from '@/components/shared/widget/TalkativeWidget.vue'

import { useTabHandler } from '@/composables/tabHandler'
import { useAuthStore } from '@/pinia/auth'
import { useCustomerStore } from '@/pinia/customer'
import { useStyleStore } from '@/pinia/style'
import { authGetCompanyWebSettings } from './api'
import { config } from './utils/config'

const styleStore = useStyleStore()
const customerStore = useCustomerStore()
const authStore = useAuthStore()

useTabHandler()

onMounted(async () => {
  await initialiseStoreData()
  authStore.login_url = ['aws-staging', 'aws-prod'].includes(config.environment.toLowerCase())
    ? '/login'
    : '/login-admin'
})

const hasTalkative = computed(() => {
  return authStore.isPortalUser && styleStore.fabricator?.name === 'endurance'
})

const showDebugBanner = computed(() => {
  return !config.environment.toLowerCase().includes('aws-prod')
})

watch(
  () => authStore.usingBrandedModeToken,
  async (usingBrandedModeToken) => {
    if (usingBrandedModeToken) {
      const response = await authGetCompanyWebSettings()
      const tagSetting = response?.companyWebSettings.find((x) => x.key === 'tagId')
      if (tagSetting) {
        window.gtag('config', 'G-' + tagSetting.value)
      }
    }
  },
  { immediate: true }
)

watch(
  () => [styleStore.fabricator_logo, customerStore.customer_logo, styleStore.customer_logo],
  async () => {
    var existingFavicons = document.querySelectorAll("link[rel*='icon']")
    existingFavicons.forEach(function (link) {
      link.remove()
    })
    var link = document.createElement('link')
    link.type = 'image/png'
    link.rel = 'icon'
    if (authStore.isPortalUser && authStore.usingBrandedModeToken) {
      link.href = customerStore.customer_logo || styleStore.customer_logo || '/favicon.ico'
    } else {
      link.href = styleStore.fabricator_logo || '/favicon.ico'
    }
    document.head.appendChild(link)
  },
  { immediate: true }
)

async function initialiseStoreData() {
  await styleStore.refresh()
}
</script>

<template>
  <div id="app" class="text-sm">
    <MaintenanceBanner />
    <DebugBanner v-if="showDebugBanner" />
    <router-view />
    <TalkativeWidget v-if="hasTalkative" />
    <QuotilaBanner v-if="!authStore.usingBrandedModeToken" />
    <CookieBanner />
  </div>
</template>

<style src="./assets/css/styles.css"></style>
